import type { Row } from '@tanstack/react-table'
import type {
  DataTableColumn,
  DataTableColumnDef,
  DataTableColumnOrderState,
  DataTableDefinedColumnDef,
  DataTableDefinedTableOptions,
  DataTableFilterOption,
  DataTableRowData,
} from '../types'

export const getColumnId = <TData extends DataTableRowData>(
  columnDef: DataTableColumnDef<TData>,
): string => columnDef.id ?? columnDef.accessorKey?.toString?.() ?? columnDef.header

export const getAllLeafColumnDefs = <TData extends DataTableRowData>(
  columns: DataTableColumnDef<TData>[],
): DataTableColumnDef<TData>[] => {
  const allLeafColumnDefs: DataTableColumnDef<TData>[] = []
  const getLeafColumns = (cols: DataTableColumnDef<TData>[]) => {
    cols.forEach((col) => {
      if (col.columns) {
        getLeafColumns(col.columns)
      } else {
        allLeafColumnDefs.push(col)
      }
    })
  }
  getLeafColumns(columns)
  return allLeafColumnDefs
}

export const prepareColumns = <TData extends DataTableRowData>({
  columnDefs,
  tableOptions,
}: {
  columnDefs: DataTableColumnDef<TData>[]
  tableOptions: DataTableDefinedTableOptions<TData>
}): DataTableDefinedColumnDef<TData>[] => {
  const {
    aggregationFns = {},
    defaultDisplayColumn,
    filterFns = {},
    sortingFns = {},
    state: { columnFilterFns = {} } = {},
  } = tableOptions
  return columnDefs.map((columnDef) => {
    // assign columnId
    if (!columnDef.id) columnDef.id = getColumnId(columnDef)
    // assign columnDefType
    if (!columnDef.columnDefType) columnDef.columnDefType = 'data'
    if (columnDef.columns?.length) {
      columnDef.columnDefType = 'group'
      // recursively prepare columns if this is a group column
      columnDef.columns = prepareColumns({
        columnDefs: columnDef.columns,
        tableOptions,
      })
    } else if (columnDef.columnDefType === 'data') {
      // assign aggregationFns if multiple aggregationFns are provided
      if (Array.isArray(columnDef.aggregationFn)) {
        const aggFns = columnDef.aggregationFn as string[]
        columnDef.aggregationFn = (
          columnId: string,
          leafRows: Row<TData>[],
          childRows: Row<TData>[],
        ) => aggFns.map((fn) => aggregationFns[fn]?.(columnId, leafRows, childRows))
      }

      // assign filterFns
      if (Object.keys(filterFns).includes(columnFilterFns[columnDef.id])) {
        columnDef.filterFn = filterFns[columnFilterFns[columnDef.id]] ?? filterFns.fuzzy
        ;(columnDef as DataTableDefinedColumnDef<TData>)._filterFn = columnFilterFns[columnDef.id]
      }

      // assign sortingFns
      if (Object.keys(sortingFns).includes(columnDef.sortingFn as string)) {
        // @ts-expect-error this is the custom Sorting Fn we have at column
        columnDef.sortingFn = sortingFns[columnDef.sortingFn]
      }
    } else if (columnDef.columnDefType === 'display') {
      columnDef = {
        ...(defaultDisplayColumn as DataTableColumnDef<TData>),
        ...columnDef,
      }
    }
    return columnDef
  }) as DataTableDefinedColumnDef<TData>[]
}

export const reorderColumn = <TData extends DataTableRowData>(
  draggedColumn: DataTableColumn<TData>,
  targetColumn: DataTableColumn<TData>,
  columnOrder: DataTableColumnOrderState,
): DataTableColumnOrderState => {
  if (draggedColumn.getCanPin()) {
    draggedColumn.pin(targetColumn.getIsPinned())
  }
  const newColumnOrder = [...columnOrder]
  newColumnOrder.splice(
    newColumnOrder.indexOf(targetColumn.id),
    0,
    newColumnOrder.splice(newColumnOrder.indexOf(draggedColumn.id), 1)[0],
  )
  return newColumnOrder
}

export const getDefaultColumnFilterFn = (): DataTableFilterOption => {
  return 'fuzzy'
}
